import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import gatewayProfileService from '../../services/gatewayProfileService'

/**
 * Helper to extract error message from various error formats
 */
const getErrorMessage = (error) => {
    if (typeof error === 'string') return error
    if (error?.message) return error.message
    if (error?.data?.message) return error.data.message
    return 'An unexpected error occurred'
}

// Async Thunks
export const fetchGatewayProfiles = createAsyncThunk(
    'gatewayProfile/fetchAll',
    async ({ page, limit, search }, { rejectWithValue }) => {
        try {
            const response = await gatewayProfileService.getAllGatewayProfiles(page, limit, search)
            return response
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

export const fetchGatewayProfileById = createAsyncThunk(
    'gatewayProfile/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await gatewayProfileService.getGatewayProfileById(id)
            return response
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

export const createGatewayProfile = createAsyncThunk(
    'gatewayProfile/create',
    async (data, { rejectWithValue }) => {
        try {
            const response = await gatewayProfileService.createGatewayProfile(data)
            return response
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

export const updateGatewayProfile = createAsyncThunk(
    'gatewayProfile/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await gatewayProfileService.updateGatewayProfile(id, data)
            return response
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

export const deleteGatewayProfile = createAsyncThunk(
    'gatewayProfile/delete',
    async (id, { rejectWithValue }) => {
        try {
            await gatewayProfileService.deleteGatewayProfile(id)
            return id
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

export const fetchGatewayProfileByAPIKey = createAsyncThunk(
    'gatewayProfile/fetchByAPIKey',
    async (apiKey, { rejectWithValue }) => {
        try {
            const response = await gatewayProfileService.getGatewayProfileByAPIKey(apiKey)
            return response
        } catch (error) {
            return rejectWithValue(getErrorMessage(error))
        }
    }
)

const initialState = {
    profiles: [],
    currentProfile: null,
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        limit: 10
    },
    createLoading: false,
    updateLoading: false,
    deleteLoading: false
}

const gatewayProfileSlice = createSlice({
    name: 'gatewayProfile',
    initialState,
    reducers: {
        clearCurrentProfile: (state) => {
            state.currentProfile = null
        },
        clearError: (state) => {
            state.error = null
        },
        resetGatewayProfileState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            // Fetch All
            .addCase(fetchGatewayProfiles.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchGatewayProfiles.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                // Response: { status, message, data: { profiles: [], pagination: {} } }
                state.profiles = action.payload?.data?.profiles || []
                const pagination = action.payload?.data?.pagination
                if (pagination) {
                    state.pagination = {
                        currentPage: pagination.page || 1,
                        totalPages: pagination.totalPages || 1,
                        totalItems: pagination.total || 0,
                        limit: pagination.limit || 10
                    }
                }
            })
            .addCase(fetchGatewayProfiles.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || 'Failed to fetch gateway profiles'
                state.profiles = []
            })
            // Fetch By Id
            .addCase(fetchGatewayProfileById.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchGatewayProfileById.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                // Response: { status, message, data: { profile: {} } } or { status, message, data: {} }
                state.currentProfile = action.payload?.data?.profile || action.payload?.data
            })
            .addCase(fetchGatewayProfileById.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || 'Failed to fetch gateway profile'
            })
            // Create
            .addCase(createGatewayProfile.pending, (state) => {
                state.createLoading = true
                state.error = null
            })
            .addCase(createGatewayProfile.fulfilled, (state, action) => {
                state.createLoading = false
                state.error = null
                // Response: { status, message, data: { profile: {} } } or { status, message, data: {} }
                const newProfile = action.payload?.data?.profile || action.payload?.data
                if (newProfile && newProfile.id) {
                    state.profiles.unshift(newProfile)
                }
            })
            .addCase(createGatewayProfile.rejected, (state, action) => {
                state.createLoading = false
                state.error = action.payload || 'Failed to create gateway profile'
            })
            // Update
            .addCase(updateGatewayProfile.pending, (state) => {
                state.updateLoading = true
                state.error = null
            })
            .addCase(updateGatewayProfile.fulfilled, (state, action) => {
                state.updateLoading = false
                state.error = null
                // Response: { status, message, data: { profile: {} } } or { status, message, data: {} }
                const updatedProfile = action.payload?.data?.profile || action.payload?.data
                if (updatedProfile && updatedProfile.id) {
                    const index = state.profiles.findIndex(p => p.id === updatedProfile.id)
                    if (index !== -1) {
                        state.profiles[index] = updatedProfile
                    }
                    state.currentProfile = updatedProfile
                }
            })
            .addCase(updateGatewayProfile.rejected, (state, action) => {
                state.updateLoading = false
                state.error = action.payload || 'Failed to update gateway profile'
            })
            // Delete
            .addCase(deleteGatewayProfile.pending, (state) => {
                state.deleteLoading = true
                state.error = null
            })
            .addCase(deleteGatewayProfile.fulfilled, (state, action) => {
                state.deleteLoading = false
                state.error = null
                state.profiles = state.profiles.filter(p => p.id !== action.payload)
            })
            .addCase(deleteGatewayProfile.rejected, (state, action) => {
                state.deleteLoading = false
                state.error = action.payload || 'Failed to delete gateway profile'
            })
            // Fetch By API Key
            .addCase(fetchGatewayProfileByAPIKey.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchGatewayProfileByAPIKey.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                state.currentProfile = action.payload?.data?.profile || action.payload?.data
            })
            .addCase(fetchGatewayProfileByAPIKey.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || 'Failed to fetch gateway profile by API key'
            })
    }
})

// Export actions
export const {
    clearCurrentProfile,
    clearError,
    resetGatewayProfileState
} = gatewayProfileSlice.actions

// Export reducer as default
export default gatewayProfileSlice.reducer